import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { getViewpoints, loadStats } from "../services/gutcheck-admin-api.service";
import { useAuth0 } from "@auth0/auth0-react";
import { Stack, Typography } from "@mui/material";
import { AdminViewpointsList } from "./components/ViewpointsList";
import { IProject, IViewpoint, ViewpointStatus, IAdvisor } from "../../../types";
import { AdvisorModal } from "../advisors/AdvisorModal";

const interestFields = [
    {
        key: "interest_demo",
        text: "Demo",
        count: 0,
        countText: "want a"
    },
    {
        key: "interest_beta",
        text: "Beta",
        count: 0,
        countText: "want to"
    },
    {
        key: "interest_investor",
        text: "Co-Invest",
        count: 0,
        countText: "want to"
    },
    {
        key: "interest_advisor",
        text: "Advise",
        count: 0,
        countText: "want to"
    },
    {
        key: "interest_employment",
        text: "Job",
        count: 0,
        countText: "want a"
    },
    {
        key: "interest_followup",
        text: "Follow Up",
        count: 0,
        countText: 'open to'
    },
];


export const AdminViewpoints = () => {

    const { project, reloadProjectData } = useOutletContext<{ project: IProject, reloadProjectData: () => void }>();
    const { getAccessTokenSilently } = useAuth0();

    const [viewpoints, setViewpoints] = useState<IViewpoint[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [ reloadError, setReloadError ] = useState<string | null>(null);
    
    // AdvisorModal state
    const [advisorModalOpen, setAdvisorModalOpen] = useState(false);
    const [selectedAdvisor, setSelectedAdvisor] = useState<IAdvisor | null>(null);

    const fetchViewpoints = async () => {
        setLoading(true);

        const accessToken = await getAccessTokenSilently();
        const { data, error } = await getViewpoints(project.id, accessToken);

        if (data) {
            setViewpoints(data.viewpoints);
            setLoading(false);
        }
    };
    
    useEffect(() => {
        if (project) {
            fetchViewpoints();
        }
    }, []);


    const handleReloadStats = async (event: any) => {
        event.preventDefault();
        setReloadError(null);

        const origText = event.target.innerText;

        event.target.innerText = "Reloading...";
        const accessToken = await getAccessTokenSilently();
        const { data, error} = await loadStats(project.id, accessToken);
        if (data) {
            const { data, error } = await getViewpoints(project.id, accessToken);

            
            if (data) {
                setViewpoints(data.viewpoints);
                setLoading(false);
                reloadProjectData();
            } else if (error) {
                console.error(error);
            }
        } else if (error) {
            console.error(error);
            setReloadError(error.message);
        }
        event.target.innerText = origText;
    }

    const handleAdvisorModalOpen = (advisor: IAdvisor) => {
        setSelectedAdvisor(advisor);
        setAdvisorModalOpen(true);
    };

    const handleAdvisorModalClose = () => {
        setAdvisorModalOpen(false);
        setSelectedAdvisor(null);
    };

    const handleAdvisorUpdate = (updatedAdvisor: IAdvisor) => {
        // Update the advisor in the viewpoints list
        setViewpoints(prevViewpoints => 
            prevViewpoints.map(viewpoint => 
                viewpoint.advisor?.id === updatedAdvisor.id 
                    ? { ...viewpoint, advisor: updatedAdvisor }
                    : viewpoint
            )
        );
    };

    const handleAdvisorModalUpdate = () => {
        // Refresh viewpoints data when advisor modal updates
        fetchViewpoints();
        // Also refresh project data
        reloadProjectData();
    };

    return (
        <div className="admin-screen">
            <div className="admin-table-header">
                <h5>Viewpoints</h5>
                <Stack direction="row" spacing={2} alignItems="center">
                    {reloadError && <Typography variant="body2" color="error">{reloadError}</Typography>}
                    <div className="button btn btn-secondary" onClick={handleReloadStats} >Reload Stats</div>
                </Stack>
            </div>
            <AdminViewpointsList
                project={project}
                sectionName="Review Queue" 
                viewpoints={viewpoints.filter((viewpoint: IViewpoint) => viewpoint.status === ViewpointStatus.COMPLETED )}
                reloadProjectData={fetchViewpoints}
                onAdvisorClick={handleAdvisorModalOpen}
            />

            <AdminViewpointsList
                project={project}
                sectionName="Approved"
                viewpoints={viewpoints.filter((viewpoint: IViewpoint) => viewpoint.status === ViewpointStatus.APPROVED)}
                reloadProjectData={fetchViewpoints}
                onAdvisorClick={handleAdvisorModalOpen}
            />

            <AdminViewpointsList
                project={project}
                sectionName="In Progress"
                viewpoints={viewpoints.filter((viewpoint: IViewpoint) => viewpoint.status === ViewpointStatus.IN_PROGRESS)}
                reloadProjectData={fetchViewpoints}
                onAdvisorClick={handleAdvisorModalOpen}
            />

            <AdminViewpointsList
                project={project}
                sectionName="Rejected"
                viewpoints={viewpoints.filter((viewpoint: IViewpoint) => viewpoint.status === ViewpointStatus.REJECTED)}
                reloadProjectData={fetchViewpoints}
                onAdvisorClick={handleAdvisorModalOpen}
            />

            {/* AdvisorModal */}
            {selectedAdvisor && (
                <AdvisorModal
                    open={advisorModalOpen}
                    onClose={handleAdvisorModalClose}
                    advisor={selectedAdvisor}
                    onAdvisorUpdate={handleAdvisorUpdate}
                />
            )}
        </div>
    );
}